import { useMemo } from 'react'

import { Empty, Spin, Typography } from 'antd'
import { createStyles } from 'antd-style'

import { BidRecordEntity } from '@eastcoal/api/trade/interface'

import { BasicProps } from '@eastcoal/common/types/component'
import { pretty } from '@eastcoal/common/utils/decimal'
import { pick } from '@eastcoal/common/utils/object'

import { useAuthStore } from '@eastcoal/auth/hooks/useStore'
import { getSeatNo } from '@eastcoal/shared/utils/auction'

import { useAuctionDetailStore } from '../../Store/hooks/useStore'

import { AuctionBidTableColumn } from './interface'

const tableHeaderBgColor = '#fafafa'

const useStyles = createStyles(({ token }) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 440,
    overflow: 'hidden',
    border: `1px solid ${token.colorBorderSecondary}`
  },
  title: {
    flex: 'none',
    paddingBlock: token.paddingMD,
    color: token.colorTextHeading,
    fontSize: token.fontSizeHeading5,
    textAlign: 'center'
  },
  wrapper: {
    flex: 1,
    overflow: 'auto',
    // fix: https://github.com/ant-design/ant-design/discussions/46176
    overflowAnchor: 'none'
  },
  spin: {
    transition: 'opacity 0.3s ease'
  },
  table: {
    position: 'relative',
    width: '100%',
    tableLayout: 'fixed',
    borderCollapse: 'separate',
    borderSpacing: 0
  },
  thead: {
    position: 'sticky',
    top: 0
  },
  th: {
    padding: token.paddingXS,
    whiteSpace: 'nowrap',
    fontWeight: 'normal',
    backgroundColor: tableHeaderBgColor,
    borderStyle: 'solid',
    borderColor: token.colorBorderSecondary,
    borderWidth: 0,
    borderBottomWidth: 1,
    [`&:not(:last-child)`]: {
      borderRightWidth: 1
    }
  },
  tbody: {
    ['tr']: {
      ['&:not(.empty-tr):hover']: {
        backgroundColor: tableHeaderBgColor
      },
      ['&.empty-tr']: {
        ['td']: {
          paddingBlock: token.paddingXL,
          borderWidth: 0
        }
      }
    },
    ['td']: {
      padding: token.paddingXS,
      borderStyle: 'solid',
      borderColor: token.colorBorderSecondary,
      borderWidth: 0,
      borderBottomWidth: 1
    }
  }
}))

export const AuctionDetailBidRecordCard = ({
  className,
  style
}: BasicProps) => {
  const { detailData, bidRecords, subscribeLoading } = useAuctionDetailStore(
    state => pick(state, ['detailData', 'bidRecords', 'subscribeLoading'])
  )

  const priceText = detailData.priceUnit
    ? `价格（${detailData.priceUnit}）`
    : '价格'

  const quantityText = detailData.quantityUnit
    ? `数量（${detailData.quantityUnit}）`
    : '数量'

  const currentProfileId = useAuthStore(state => state.profileId)

  const columns = useMemo<AuctionBidTableColumn<BidRecordEntity>[]>(
    () => [
      {
        dataIndex: 'applyId',
        title: '用户',
        align: 'center',
        width: 100,
        render(_, { profileId, applyId = '' }) {
          return profileId === currentProfileId ? getSeatNo(applyId) : '******'
        }
      },
      {
        dataIndex: 'price',
        title: (
          <Typography.Text
            ellipsis={{
              tooltip: priceText
            }}
          >
            {priceText}
          </Typography.Text>
        ),
        align: 'right',
        render(value) {
          return pretty(value)
        }
      },
      {
        dataIndex: 'quantity',
        title: (
          <Typography.Text
            ellipsis={{
              tooltip: quantityText
            }}
          >
            {quantityText}
          </Typography.Text>
        ),
        align: 'right',
        render(value) {
          return pretty(value)
        }
      },
      {
        dataIndex: 'bidTime',
        title: '时间',
        align: 'center',
        render(value) {
          const parts = value.split(' ')
          return `${parts[1]} ${parts[2]}`
        }
      }
    ],
    [priceText, quantityText, currentProfileId]
  )

  const { cx, styles } = useStyles()

  const theadDom = useMemo(() => {
    return (
      <thead className={styles.thead}>
        <tr>
          {columns.map(({ dataIndex, title, width, align }) => (
            <th
              key={dataIndex}
              className={styles.th}
              scope='col'
              style={{ textAlign: align, width }}
            >
              {title}
            </th>
          ))}
        </tr>
      </thead>
    )
  }, [columns, styles])

  const tbodyDom = (
    <tbody className={styles.tbody}>
      {bidRecords.length ? (
        bidRecords.map((record, index) => {
          return (
            <tr key={record.id}>
              {columns.map(({ dataIndex, render, align }) => (
                <td
                  key={dataIndex}
                  style={{
                    textAlign: align
                  }}
                >
                  {render
                    ? render(record[dataIndex], record, index)
                    : (record[dataIndex] as any)}
                </td>
              ))}
            </tr>
          )
        })
      ) : (
        <tr className='empty-tr'>
          <td colSpan={columns.length} style={{ textAlign: 'center' }}>
            <Empty description='暂无出价记录' />
          </td>
        </tr>
      )}
    </tbody>
  )

  return (
    <div className={cx(styles.container, className)} style={style}>
      <div className={styles.title}>出价记录</div>
      <div className={styles.wrapper}>
        <Spin
          spinning={subscribeLoading}
          classNames={{
            root: styles.spin
          }}
        >
          <table className={styles.table}>
            {theadDom}
            {tbodyDom}
          </table>
        </Spin>
      </div>
    </div>
  )
}
